import { Box, OutlinedInput, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { ChangeEvent, FC, useMemo, useState } from 'react';
import {
  ScheduleFrequency,
  ScheduleTimezone,
  ScheduleValue,
  Weekday,
} from '../../../schemas/SourcingList.schema';
import { dialogInputSx } from '../sourcing-dialog.styles';
import {
  buildStartAtUtc,
  clamp,
  DEFAULT_TIMEZONE,
  emptyDraft,
  FREQUENCIES,
  parseTime,
  startSchedule,
  TIMEZONES,
  toggleWeekday,
  transitionFrequency,
  WEEKDAYS,
} from './schedule';

// Each group's min-width is sized to its longest label (equal-width effect).
const REFRESH_BUTTON_MIN_WIDTH = 148;
const FREQUENCY_BUTTON_MIN_WIDTH = 84;
const WEEKDAY_BUTTON_MIN_WIDTH = 48;
const TIMEZONE_BUTTON_MIN_WIDTH = 48;
const TIME_FIELD_WIDTH = 52;
const AMPM_TZ_GAP_PX = 14;

export interface ScheduleBlockProps {
  readonly value: ScheduleValue;
  readonly onChange: (next: ScheduleValue) => void;
  readonly disabled?: boolean;
  readonly subjectLabel?: string | null;
  readonly hasSavedSchedule?: boolean;
}

export const ScheduleBlock: FC<ScheduleBlockProps> = ({
  value,
  onChange,
  disabled = false,
  subjectLabel,
  hasSavedSchedule = false,
}) => {
  const mode: 'schedule' | 'manual' = value === null ? 'manual' : 'schedule';
  const timezone: ScheduleTimezone = value?.timezone ?? DEFAULT_TIMEZONE;
  const time = useMemo(
    () => (value?.startAtUtc ? parseTime(value.startAtUtc, timezone) : null),
    [value, timezone]
  );

  const handleModeChange = (_: unknown, next: 'schedule' | 'manual' | null) => {
    if (next === null) {
      return;
    }
    onChange(next === 'manual' ? null : (value ?? emptyDraft()));
  };

  const handleFrequencyChange = (_: unknown, next: ScheduleFrequency | null) => {
    if (!value || next === null) {
      return;
    }
    if (value.frequency === null) {
      onChange(startSchedule(next, value));
      return;
    }
    onChange(transitionFrequency(value, next));
  };

  const handleTimezoneChange = (_: unknown, next: ScheduleTimezone | null) => {
    if (!value || value.frequency === null || next === null) {
      return;
    }
    onChange({ ...value, timezone: next });
  };

  const handleWeekdayClick = (day: Weekday) => {
    if (!value || value.frequency === null) {
      return;
    }
    onChange(toggleWeekday(value, day));
  };

  const handleHourCommit = (hour12: number) => {
    if (!value || value.frequency === null) {
      return;
    }
    onChange({ ...value, startAtUtc: buildStartAtUtc(hour12, time?.ampm ?? 'AM', timezone) });
  };

  const handleAmpmChange = (_: unknown, next: 'AM' | 'PM' | null) => {
    if (!value || value.frequency === null || next === null) {
      return;
    }
    onChange({ ...value, startAtUtc: buildStartAtUtc(time?.hour12 ?? 9, next, timezone) });
  };

  const handleDayOfMonthCommit = (dayOfMonth: number) => {
    if (!value || value.frequency === null) {
      return;
    }
    onChange({ ...value, dayOfMonth });
  };

  const selectedDays = new Set(value?.daysOfWeek ?? []);
  const showFrequencyPicker = value !== null;
  const showWeekdays = value?.frequency === 'DAILY' || value?.frequency === 'WEEKLY';
  const showDayOfMonth = value?.frequency === 'MONTHLY';
  const showEmptyHint = !hasSavedSchedule;

  return (
    <Stack spacing={5}>
      {showEmptyHint && (
        <Typography variant='body1' color='text.primary'>
          No schedule has been set for this list yet.
        </Typography>
      )}

      <Box>
        {subjectLabel && <FieldLabel text={subjectLabel} />}
        <ToggleButtonGroup
          exclusive
          color='secondary'
          value={mode}
          onChange={handleModeChange}
          disabled={disabled}
          sx={{ '& .MuiToggleButtonGroup-grouped': { minWidth: REFRESH_BUTTON_MIN_WIDTH } }}
        >
          <ToggleButton value='schedule'>Scheduled</ToggleButton>
          <ToggleButton value='manual'>Manual Only</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {showFrequencyPicker && (
        <>
          <Box>
            <FieldLabel text='Frequency' />
            <ToggleButtonGroup
              exclusive
              color='secondary'
              value={value?.frequency ?? null}
              onChange={handleFrequencyChange}
              disabled={disabled}
              sx={{ '& .MuiToggleButtonGroup-grouped': { minWidth: FREQUENCY_BUTTON_MIN_WIDTH } }}
            >
              {FREQUENCIES.map((f) => (
                <ToggleButton key={f.value} value={f.value}>
                  {f.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {showWeekdays && value && (
            <Box>
              <FieldLabel text={value.frequency === 'WEEKLY' ? 'On' : 'Repeat on'} />
              <ToggleButtonGroup
                color='secondary'
                sx={{ '& .MuiToggleButtonGroup-grouped': { minWidth: WEEKDAY_BUTTON_MIN_WIDTH } }}
              >
                {WEEKDAYS.map((d) => {
                  const selected = selectedDays.has(d.value);
                  return (
                    <ToggleButton
                      key={d.value}
                      value={d.value}
                      selected={selected}
                      onClick={() => handleWeekdayClick(d.value)}
                      disabled={disabled}
                      aria-pressed={selected}
                    >
                      {d.label}
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
            </Box>
          )}

          {showDayOfMonth && value && (
            <Box>
              <FieldLabel text='On day of month' />
              <NumericInput
                value={value.dayOfMonth}
                min={1}
                max={31}
                onCommit={handleDayOfMonthCommit}
                disabled={disabled}
                width={TIME_FIELD_WIDTH}
              />
            </Box>
          )}

          {time && (
            <Box>
              <FieldLabel text='At' />
              <Stack direction='row' alignItems='center' sx={{ gap: `${AMPM_TZ_GAP_PX}px` }}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <NumericInput
                    value={time.hour12}
                    min={1}
                    max={12}
                    onCommit={handleHourCommit}
                    disabled={disabled}
                    width={TIME_FIELD_WIDTH}
                  />
                  <ToggleButtonGroup
                    exclusive
                    color='secondary'
                    value={time.ampm}
                    onChange={handleAmpmChange}
                    disabled={disabled}
                    sx={{ '& .MuiToggleButtonGroup-grouped': { minWidth: TIME_FIELD_WIDTH } }}
                  >
                    <ToggleButton value='AM'>AM</ToggleButton>
                    <ToggleButton value='PM'>PM</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <ToggleButtonGroup
                  exclusive
                  color='secondary'
                  value={timezone}
                  onChange={handleTimezoneChange}
                  disabled={disabled}
                  aria-label='Timezone'
                  sx={{ '& .MuiToggleButtonGroup-grouped': { minWidth: TIMEZONE_BUTTON_MIN_WIDTH } }}
                >
                  {TIMEZONES.map((tz) => (
                    <ToggleButton key={tz.value} value={tz.value} aria-label={tz.label}>
                      {tz.value}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            </Box>
          )}
        </>
      )}
    </Stack>
  );
};

const FieldLabel: FC<{ text: string }> = ({ text }) => (
  <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: '4px' }}>
    {text}
  </Typography>
);

interface NumericInputProps {
  readonly value: number | null;
  readonly min: number;
  readonly max: number;
  readonly onCommit: (next: number) => void;
  readonly disabled?: boolean;
  readonly width: number;
}

// `draft === null` means "show the prop"; while editing, it holds the typed string so the
// user can clear/retype freely. On blur we commit (clamped) or revert by clearing draft.
const NumericInput: FC<NumericInputProps> = ({ value, min, max, onCommit, disabled, width }) => {
  const [draft, setDraft] = useState<string | null>(null);
  const display = draft ?? (value === null ? '' : String(value));

  const commit = () => {
    if (draft === null) {
      return;
    }
    if (draft !== '') {
      const parsed = Number(draft);
      if (Number.isFinite(parsed)) {
        const next = clamp(Math.floor(parsed), min, max);
        if (next !== value) {
          onCommit(next);
        }
      }
    }
    setDraft(null);
  };

  return (
    <OutlinedInput
      type='number'
      value={display}
      onChange={(e: ChangeEvent<HTMLInputElement>) => setDraft(e.target.value)}
      onBlur={commit}
      inputProps={{ min, max }}
      disabled={disabled}
      sx={{
        ...dialogInputSx,
        width,
        height: 'auto',
        minHeight: 36,
        '& .MuiOutlinedInput-input': { padding: '6px 12px' },
      }}
    />
  );
};
